import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader } from 'lucide-react';
import * as z from 'zod';
import store from '../../../rematch';
import axiosClient from '../../../api/axiosClient';
import { removeCookie } from '../../../utils';
import { safeWindowLocation } from '../../../utils/safeNavigation';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { Form } from '../../ui/form';
import BasicSelect from '../../../pages/OnboardingSteps/Component/BasicSelect';
import {
  FRL_OPTIONS,
  FRL_QUESTION_LABEL,
  PARENT_EDUCATION_OPTIONS,
  PARENT_EDUCATION_QUESTION_LABEL,
  PROFILE_QUESTION,
  PROFILE_QUESTIONS_SAVE_ERROR,
  buildProfileQuestionsPayload,
  getPendingProfileQuestions,
} from '../../../utils/profileQuestions';

const QUESTION_ROWS = {
  [PROFILE_QUESTION.FRL]: {
    key: PROFILE_QUESTION.FRL,
    label: FRL_QUESTION_LABEL,
    options: FRL_OPTIONS,
  },
  [PROFILE_QUESTION.PARENT_EDUCATION]: {
    key: PROFILE_QUESTION.PARENT_EDUCATION,
    label: PARENT_EDUCATION_QUESTION_LABEL,
    options: PARENT_EDUCATION_OPTIONS,
  },
};

const REQUIRED_MESSAGE = 'Please select a response';
export const ANSWER_ALL_HINT = 'Answer all questions to continue.';
const HINT_ID = 'profile-questions-hint';
export const PROFILE_QUESTIONS_SAVED = 'Thanks! Your answers have been saved.';

const buildSchema = pending =>
  z.object(
    Object.fromEntries(
      pending.map(question => [
        question,
        z.string({ required_error: REQUIRED_MESSAGE }).min(1, { message: REQUIRED_MESSAGE }),
      ])
    )
  );

// The mentee has to answer before using the app, so every way of dismissing the dialog is blocked.
const preventDismiss = event => event.preventDefault();

const TABBABLE = 'input:not([type="hidden"]), button, select, textarea, a[href], [tabindex]';

/**
 * Tab out of a react-select inside the dialog, moving focus ourselves.
 *
 * react-select removes its screen-reader text when it blurs, and the dialog's focus trap moves focus
 * to the dialog box whenever nodes are removed while nothing is focused. A browser Tab blurs the select
 * before focusing the next control, so it always ended on the dialog box instead. Focusing the next
 * control during the keydown leaves no unfocused moment. A Tab react-select already handled (picking an
 * option from an open menu) is left alone.
 *
 * The focus trap's own Tab handler runs on the same element right after this one and ignores
 * defaultPrevented. Focusing straight away would make it see the new control as the first or last one and
 * wrap again, so focus moves in a microtask after it. The prevented keydown keeps the select focused until then.
 */
export const tabOutOfSelect = event => {
  const { target, currentTarget } = event;
  if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
  if (target.tagName !== 'INPUT' || !target.closest('.react_dropdown')) return;
  const tabbables = [...currentTarget.querySelectorAll(TABBABLE)].filter(
    element => element.tabIndex >= 0 && !element.disabled && element.getClientRects().length > 0
  );
  const index = tabbables.indexOf(target);
  if (index === -1) return;
  const next = tabbables[(index + (event.shiftKey ? -1 : 1) + tabbables.length) % tabbables.length];
  event.preventDefault();
  queueMicrotask(() => next.focus());
};
const noop = () => {};

/**
 * The header's logout is unreachable while this dialog is open, so the dialog offers its own.
 * The local session is cleared even if the API call fails, so a mentee is never stuck here.
 */
export const logOut = async () => {
  let sloUrl;
  try {
    const res = await axiosClient.get('auth/log-out');
    sloUrl = res?.data?.data?.sloLogoutUrl;
  } catch (error) {
    console.error(error);
  }
  removeCookie('userDetails');
  localStorage.setItem('userStatus', 'logged Out');
  store.dispatch({ type: 'RESET' });
  if (!(sloUrl && safeWindowLocation(sloUrl, 'assign'))) {
    window.location.assign('/');
  }
};

const ProfileQuestionsForm = ({ pending }) => {
  const submitting = useSelector(state => state.loading.effects.authModel.submitProfileQuestions);
  const form = useForm({
    resolver: zodResolver(buildSchema(pending)),
    defaultValues: Object.fromEntries(pending.map(question => [question, ''])),
  });

  const [submitError, setSubmitError] = useState('');
  const answers = form.watch(pending);
  const allAnswered = pending.every((_, index) => typeof answers[index] === 'string' && answers[index].trim() !== '');

  const onSubmit = async values => {
    setSubmitError('');
    const result = await store.dispatch.authModel.submitProfileQuestions({
      payload: buildProfileQuestionsPayload(pending, values),
    });
    if (!result?.ok) {
      setSubmitError(result?.message || PROFILE_QUESTIONS_SAVE_ERROR);
      return;
    }
    // The dialog closes on its own once nothing is pending, so confirm the save outside it.
    store.dispatch.toastModel.showToast({ value: 'Success', message: PROFILE_QUESTIONS_SAVED });
  };

  return (
    <Dialog open onOpenChange={noop}>
      <DialogContent
        hideClose
        onEscapeKeyDown={preventDismiss}
        onPointerDownOutside={preventDismiss}
        onInteractOutside={preventDismiss}
        onKeyDown={tabOutOfSelect}
        className="max-w-[560px] rounded-2xl p-[3.2rem] gap-[1.6rem] focus:outline-none">
        {/* The app sets html font-size to 10px, so sizes are in rem against that, like the rest of the app.
            The dialog box itself takes focus when an outside click is blocked. It is never a Tab stop, so it
            gets no outline: the controls inside keep their own focus styles. */}
        <DialogTitle className="montserrat_bold text-[2.2rem] leading-[1.3]">Before you continue</DialogTitle>
        <DialogDescription className="text-[1.6rem] leading-[1.5]">
          {/* Placeholder copy until product provides the final text. */}
          Please answer the following questions to continue.
        </DialogDescription>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
            {pending.map(question => (
              <BasicSelect
                key={question}
                form={form}
                row={{
                  ...QUESTION_ROWS[question],
                  required: true,
                  placeholder: 'Select Response',
                  wrapperClass: 'w_100',
                }}
                getComponent={noop}
              />
            ))}
            {/* Rendered inside the dialog: anything outside it is hidden from screen readers while it is open. */}
            <p role="alert" className="text-[1.4rem] error mt-4">
              {submitError}
            </p>
            <div className="flex flex-col items-center gap-[1.2rem] mt-4">
              {/* aria-disabled rather than disabled while answers are missing: the button stays reachable by
                  keyboard and screen readers, announces the hint, and a press shows which question is missing. */}
              <button
                type="submit"
                // The app's .btn rule sets its own background and hover colours, so these need !important.
                className={`btn !w-full ${
                  allAnswered ? '!bg-[#1373e7] !text-white' : '!bg-[#e0e0e0] !text-[#767676] cursor-not-allowed'
                }`}
                aria-disabled={!allAnswered}
                aria-describedby={allAnswered ? undefined : HINT_ID}
                aria-busy={!!submitting}
                disabled={!!submitting}>
                {submitting ? (
                  <span className="inline-flex items-center justify-center gap-[0.8rem]">
                    <Loader className="animate-spin h-[1.8rem] w-[1.8rem]" aria-hidden="true" />
                    Saving…
                  </span>
                ) : (
                  'Submit'
                )}
              </button>
              {/* Not shown on screen, but read out with the button so the grey state is explained. */}
              {!allAnswered && (
                <span id={HINT_ID} className="sr-only">
                  {ANSWER_ALL_HINT}
                </span>
              )}
              <button
                type="button"
                className="bg-transparent border-0 p-0 text-[1.4rem] underline text-[#767676] cursor-pointer"
                onClick={logOut}>
                Log out
              </button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

/** Blocking modal for mentees who still have profile questions to answer (`pendingProfileQuestions`). */
const ProfileQuestionsPopup = () => {
  const logInUser = useSelector(state => state.authModel.logInUser);
  const pending = useMemo(() => getPendingProfileQuestions(logInUser), [logInUser]);

  if (!pending.length) {
    return null;
  }
  // Remount when the list changes so the form schema always matches the questions shown.
  return <ProfileQuestionsForm key={pending.join(',')} pending={pending} />;
};

export default ProfileQuestionsPopup;
